import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    DataFirstStatus,
    EmploymentVerifyAndConsentRequest,
    EmploymentVerifyAndConsentResponse,
    ENVIRONMENT,
    FinancialAccount,
    FinancialInstitution,
    FinancialVerificationLoginRequest,
    FinancialVerificationLoginResponse,
    FinancialVerificationMezzoRequest,
    FinancialVerificationMezzoResponse,
    GenerateReportRequest,
    LinkAccountsRequest,
    LoginStatusResponse,
    VerifyAndConsentRequest,
    VerifyAndConsentResponse,
} from '@ld.consumer-portal/models';
import { LoggingService } from '@ld.consumer-portal/utils';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DataFirstService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getDataFirstStatus(loanId: string, borrowerId: string): Promise<DataFirstStatus> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/loans/${loanId}/status`;

    try {
      return await firstValueFrom(this.http.get<DataFirstStatus>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error getting DataFirst status for loan ${loanId}`);
      throw new Error(`Failed to get DataFirst status: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getFinancialVerificationUrl(
    request: FinancialVerificationLoginRequest
  ): Promise<FinancialVerificationLoginResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/financialVerification/institutionLoginUrl`;

    const params = new HttpParams()
      .set('borrowerId', request.borrowerId)
      .set('loanId', request.loanId)
      .set('institutionId', request.institutionId)
      .set('providerId', request.providerId || '')
      .set('mfa', (request.mfa || false).toString());

    try {
      return await firstValueFrom(this.http.get<FinancialVerificationLoginResponse>(url, { params }));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error getting financial verification URL`);
      throw new Error(`Failed to get verification URL: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async searchFinancialInstitutions(
    loanId: string,
    borrowerId: string,
    filter?: string
  ): Promise<FinancialInstitution[]> {
    let url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/financialVerification/institutions/${loanId}/${borrowerId}`;
    if (filter) {
      url += `?filter=${encodeURIComponent(filter)}`;
    }

    try {
      return await firstValueFrom(this.http.get<FinancialInstitution[]>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error searching financial institutions`);
      throw new Error(`Failed to search institutions: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getPopularInstitutions(loanId: string, borrowerId: string): Promise<FinancialInstitution[]> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/financialVerification/popularInstitutions/${loanId}/${borrowerId}`;

    try {
      return await firstValueFrom(this.http.get<FinancialInstitution[]>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error getting popular institutions`);
      throw new Error(`Failed to get popular institutions: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async checkLoginStatus(orderId: string, loginId: string): Promise<LoginStatusResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/financialVerification/orders/${orderId}/loginStatus/${loginId}`;

    try {
      return await firstValueFrom(this.http.get<LoginStatusResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error checking login status for order ${orderId}`);
      throw new Error(`Failed to check login status: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getAccounts(orderId: string, institutionId: string): Promise<FinancialAccount[]> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/financialVerification/orders/${orderId}/accounts/${institutionId}`;

    try {
      return await firstValueFrom(this.http.get<FinancialAccount[]>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error getting accounts for order ${orderId}`);
      throw new Error(`Failed to get accounts: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async linkAccounts(orderId: string, request: LinkAccountsRequest): Promise<void> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/financialVerification/orders/${orderId}/accounts`;

    try {
      await firstValueFrom(this.http.post<void>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error linking accounts for order ${orderId}`);
      throw new Error(`Failed to link accounts: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async generateReport(request: GenerateReportRequest): Promise<void> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/financialVerification/generatereport`;

    try {
      await firstValueFrom(this.http.post<void>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error generating report`);
      throw new Error(`Failed to generate report: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async verifyAndConsent(request: VerifyAndConsentRequest): Promise<VerifyAndConsentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/consent`;
    const headers: Record<string, string> = {};
    if (request.token) {
      headers['Authorization'] = `Bearer ${request.token}`;
    }

    try {
      return await firstValueFrom(this.http.post<VerifyAndConsentResponse>(url, request, { headers }));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error in verify and consent`);
      throw new Error(`Failed to verify and consent: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async employmentVerifyAndConsent(request: EmploymentVerifyAndConsentRequest): Promise<EmploymentVerifyAndConsentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/mezzo/consent`;
    const headers: Record<string, string> = {};
    if (request.token) {
      headers['Authorization'] = `Bearer ${request.token}`;
    }
    try {
      return await firstValueFrom(this.http.post<EmploymentVerifyAndConsentResponse>(url, request, { headers }));
    } catch (error: unknown) {
      this.loggingService.logException(error, 'DataFirstService.employmentVerifyAndConsent');
      throw new Error(`Failed to employment verify and consent: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  

  async generateMezzoUrl(request: FinancialVerificationMezzoRequest): Promise<FinancialVerificationMezzoResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/financialVerification/mezzo/${request.voxOrderId}/generateurl`;

    try {
      return await firstValueFrom(this.http.get<FinancialVerificationMezzoResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `DataFirstService.generateMezzoUrl(${request.voxOrderId})`);
      throw new Error(`Failed to generate Mezzo Url: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async acceptInvitation(invitationId: string): Promise<void> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/datafirst/invitations/${invitationId}/accept`;

    try {
      await firstValueFrom(this.http.get<void>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error accepting invitation ${invitationId}`);
      throw new Error(`Failed to accept invitation: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}
